import { useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, CircularProgress, Divider,
  List, ListItem, ListItemIcon, ListItemText, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '@src/api/clients';

// Exclusion reasons in display order; colours are muted so the targeted
// share reads as the one saturated block in each bar.
const REASON_ORDER = ['targeted', 'income', 'home_value', 'ownership', 'single_family', 'non_residential', 'paused', 'excluded', 'scheduled', 'other'];
const REASON_COLOR = {
  targeted: 'success.main',
  income: 'warning.light',
  home_value: 'warning.main',
  ownership: 'info.light',
  single_family: 'info.main',
  non_residential: 'grey.400',
  paused: 'grey.500',
  excluded: 'grey.600',
  scheduled: 'secondary.light',
  other: 'grey.300',
};

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const pct = (num, den) => (den ? Math.round((100 * num) / den) : 0);

function ShareBar({ reasons, population, labels, height = 14 }) {
  const parts = REASON_ORDER.filter((k) => reasons[k]?.population).map((k) => ({
    key: k, pop: reasons[k].population, zips: reasons[k].zips,
  }));
  if (!population) return null;
  return (
    <Box display="flex" width="100%" height={height} borderRadius={1} overflow="hidden" bgcolor="grey.200">
      {parts.map((p) => (
        <Tooltip key={p.key} title={`${labels[p.key]}: ${fmt(p.pop)} people in ${fmt(p.zips)} ZIPs (${pct(p.pop, population)}%)`} arrow>
          <Box sx={{ width: `${(100 * p.pop) / population}%`, bgcolor: REASON_COLOR[p.key], minWidth: p.pop ? 2 : 0 }} />
        </Tooltip>
      ))}
    </Box>
  );
}

function ReasonChips({ reasons, labels }) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75}>
      {REASON_ORDER.filter((k) => k !== 'targeted' && reasons[k]?.zips).map((k) => (
        <Chip
          key={k} size="small" variant="outlined"
          label={`${labels[k]}: ${fmt(reasons[k].zips)} ZIPs`}
          sx={{ '& .MuiChip-label': { fontSize: 12 } }}
          icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: REASON_COLOR[k], ml: '6px !important' }} />}
        />
      ))}
    </Stack>
  );
}

function CriteriaList({ lines }) {
  return (
    <List dense disablePadding>
      {lines.map((line) => (
        <ListItem key={line} disableGutters sx={{ py: 0.25 }}>
          <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon fontSize="small" color="success" /></ListItemIcon>
          <ListItemText primary={line} primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
      ))}
    </List>
  );
}

function StaffRule({ rule, overrides, label }) {
  if (!rule) return null;
  const entries = Object.entries(overrides || {});
  return (
    <Box mt={1}>
      <Typography variant="caption" color="text.secondary">
        {label} rule source: <b>{rule.source}</b>
        {' · '}income ≥ {rule.income_floor_ratio ?? '—'}× · home value ≥ {rule.home_value_floor_ratio ?? '—'}×
        {' · '}own ≥ {rule.min_home_ownership_percentage ?? '—'}% · SFH ≥ {rule.min_sfh_percentage ?? '—'}%
        {rule.income_ceiling_ratio ? ` · income ≤ ${rule.income_ceiling_ratio}×` : ''}
        {rule.home_value_ceiling_ratio ? ` · home value ≤ ${rule.home_value_ceiling_ratio}×` : ''}
      </Typography>
      {entries.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block">
          Overrides: {entries.map(([k, v]) => `${k}=${v}`).join(', ')}
        </Typography>
      )}
    </Box>
  );
}

function MisplacedTable({ rows, isStaff }) {
  if (!rows?.length) return null;
  return (
    <Box mt={2}>
      <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
        <WarningAmberIcon fontSize="small" color="warning" />
        <Typography variant="subtitle2">ZIPs that sit apart from this territory</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        These ZIPs are far from the rest of the territory. They may be intentional outposts or may have been added by mistake.
        {isStaff ? ' Review with manage.py detect_misplaced_zips; --apply excludes named ZIPs.' : ' Contact us if any should be removed.'}
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ZIP</TableCell><TableCell>City</TableCell><TableCell>State</TableCell>
              <TableCell align="right">Miles from territory</TableCell><TableCell align="right">Miles to nearest ZIPs</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.zip}>
                <TableCell>{r.zip}</TableCell><TableCell>{r.city || ''}</TableCell><TableCell>{r.state || ''}</TableCell>
                <TableCell align="right">{fmt(Math.round(r.centre_miles))}</TableCell>
                <TableCell align="right">{fmt(Math.round(r.nearest_miles))}</TableCell>
                <TableCell><Chip size="small" label={r.targeted ? 'Targeted' : 'Not targeted'} color={r.targeted ? 'warning' : 'default'} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

export default function ZipFilteringTab({ clientId, isStaff }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!clientId) { setData(null); return undefined; }
    let cancelled = false;
    setLoading(true); setError('');
    api.request({ url: `dashboard/client-zips/filter-summary/?client_id=${clientId}`, method: 'GET' })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || 'Could not load filtering summary.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  const labels = data?.reason_labels || {};
  const totals = useMemo(() => {
    if (!data) return null;
    const pop = data.verticals.reduce((s, v) => s + v.population, 0);
    const popT = data.verticals.reduce((s, v) => s + v.population_targeted, 0);
    const misplaced = data.verticals.reduce((s, v) => s + v.misplaced_count, 0);
    return { pop, popT, misplaced };
  }, [data]);

  if (!clientId) return <Alert severity="info">Pick a client to see how its ZIP list is filtered.</Alert>;
  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data || data.verticals.length === 0) return <Alert severity="info">No active ZIPs yet. Filtering appears once ZIPs are on the list.</Alert>;

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>How your ZIP list is filtered</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>
          Every ZIP on your list is checked against the household profile that closes for your vertical in your market:
          income and home values relative to the local metro, and enough owner-occupied single-family homes to keep
          renters and condos out of the audience. ZIPs that pass are targeted; the rest stay on the list so nothing is lost,
          but ads do not run there. Thresholds are set per market, so a ZIP that fails in one metro can pass in another.
        </Typography>
        <Stack direction="row" gap={3} mt={2} flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary">Population targeted</Typography>
            <Typography variant="h5" fontWeight={600}>{pct(totals.popT, totals.pop)}%</Typography>
            <Typography variant="caption" color="text.secondary">{fmt(totals.popT)} of {fmt(totals.pop)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Verticals</Typography>
            <Typography variant="h5" fontWeight={600}>{data.verticals.length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">ZIPs to review</Typography>
            <Typography variant="h5" fontWeight={600} color={totals.misplaced ? 'warning.main' : 'text.primary'}>{totals.misplaced}</Typography>
            <Typography variant="caption" color="text.secondary">far from their territory</Typography>
          </Box>
        </Stack>
      </Paper>

      {data.verticals.map((v) => (
        <Paper key={v.vertical_id} variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Typography variant="h6" fontWeight={600}>{v.vertical}</Typography>
            <Typography variant="body2" color="text.secondary">
              {fmt(v.zips_targeted)} of {fmt(v.zips)} ZIPs targeted · {pct(v.population_targeted, v.population)}% of population
            </Typography>
          </Stack>
          <Box my={1.5}><ShareBar reasons={v.reasons} population={v.population} labels={labels} /></Box>
          <ReasonChips reasons={v.reasons} labels={labels} />
          {isStaff && (
            <Box mt={1}>
              <StaffRule rule={v.staff?.rule} overrides={v.staff?.client_override} label="Vertical" />
              {v.staff?.leak && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Last {v.staff.leak.days} days: {fmt(v.staff.leak.in_area_leads)} in-area leads, {fmt(v.staff.leak.leaked_leads)} from excluded ZIPs
                  {v.staff.leak.leak_pct != null ? ` (${v.staff.leak.leak_pct}% leak)` : ''}
                </Typography>
              )}
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>By territory</Typography>
          {v.territories.map((t) => (
            <Accordion key={`${v.vertical_id}-${t.territory_id ?? 'none'}`} disableGutters variant="outlined" sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box width="100%" pr={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="body1" fontWeight={500}>{t.territory}</Typography>
                      {t.misplaced.length > 0 && (
                        <Chip size="small" color="warning" variant="outlined" icon={<WarningAmberIcon />} label={`${t.misplaced.length} to review`} />
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {fmt(t.zips_targeted)}/{fmt(t.zips)} ZIPs · {pct(t.population_targeted, t.population)}% of population
                    </Typography>
                  </Stack>
                  <Box mt={1}><ShareBar reasons={t.reasons} population={t.population} labels={labels} height={10} /></Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={3}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>What a ZIP needs here</Typography>
                    <CriteriaList lines={t.criteria} />
                    {t.reference?.dma && (
                      <Typography variant="caption" color="text.secondary">
                        Reference market: {t.reference.dma}
                        {isStaff && t.reference.income_median ? ` · median income $${fmt(t.reference.income_median)} · median home $${fmt(t.reference.home_value_median)} (${t.reference.scope})` : ''}
                      </Typography>
                    )}
                    {isStaff && <StaffRule rule={t.staff?.rule} overrides={t.staff?.territory_override} label="Territory" />}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Why ZIPs are not targeted</Typography>
                    {Object.keys(t.reasons).filter((k) => k !== 'targeted').length === 0
                      ? <Typography variant="body2" color="text.secondary">Every ZIP in this territory is targeted.</Typography>
                      : <ReasonChips reasons={t.reasons} labels={labels} />}
                  </Box>
                </Box>
                <MisplacedTable rows={t.misplaced} isStaff={isStaff} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      ))}
    </Box>
  );
}
